import { useState } from "react";
import { useUpdate, useNotify } from "ra-core";
import {
  Sparkles,
  Brain,
  Zap,
  Users,
  Compass,
  HeartHandshake,
  Edit3,
  Sliders,
  ShieldAlert,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import type { Contact } from "../types";

export interface FiveLensProfile {
  core_type?: number;
  wing?: number;
  archetype?: string;
  subtype?: string;
  energy?: number;
  social?: number;
  processing?: number;
  action?: number;
  resonance?: number;
  coaching_tips?: string;
}

export const FiveLensProfileCard = ({ contact }: { contact: Contact }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const profile: FiveLensProfile = (contact as any)?.five_lens_profile || {};
  const stakeholderRole: string = (contact as any)?.stakeholder_role || "champion";
  const [update] = useUpdate();
  const notify = useNotify();

  // Form state for editing
  const [formValues, setFormValues] = useState<FiveLensProfile>({
    core_type: profile.core_type || 8,
    wing: profile.wing || 7,
    archetype: profile.archetype || "The Challenger / Strategic Catalyst",
    subtype: profile.subtype || "Social",
    energy: profile.energy || 85,
    social: profile.social || 80,
    processing: profile.processing || 85,
    action: profile.action || 90,
    resonance: profile.resonance || 75,
    coaching_tips: profile.coaching_tips || "",
  });
  const [formRole, setFormRole] = useState(stakeholderRole);

  const handleSave = async () => {
    try {
      await update(
        "contacts",
        {
          id: contact.id,
          data: {
            five_lens_profile: formValues,
            stakeholder_role: formRole,
          },
          previousData: contact,
        },
        { returnPromise: true },
      );
      notify("Five Lens psychometric profile updated successfully", {
        type: "success",
      });
      setIsEditOpen(false);
    } catch {
      notify("Failed to update profile", { type: "error" });
    }
  };

  const hasProfile = Boolean(profile.core_type || profile.energy);

  const roleLabel =
    stakeholderRole === "sponsor"
      ? "👑 Executive Sponsor"
      : stakeholderRole === "decision_maker"
      ? "🎯 Decision Maker"
      : stakeholderRole === "participant"
      ? "🎓 Cohort Learner / Coach"
      : "⚡ Internal Champion";

  return (
    <>
      <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 via-background to-purple-500/5 p-4 my-4 shadow-xs">
        {/* Card Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap pb-3 border-b border-indigo-500/20">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400">
              <Compass className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold text-sm tracking-tight text-foreground flex items-center gap-1.5">
                  Five Lens Enneagram™ Matrix
                </h4>
                <Badge className="bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30 text-[11px] py-0 px-2 font-medium">
                  {roleLabel}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Executive psychometric profiling, behavioral dynamics & leadership coaching hooks
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditOpen(true)}
              className="h-7 text-xs gap-1 border-indigo-500/30 bg-indigo-500/5 hover:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300"
            >
              <Edit3 className="w-3.5 h-3.5" />
              {hasProfile ? "Edit Profile" : "Log Profile"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Card Body */}
        {isExpanded && (
          <div className="pt-3.5 space-y-4">
            {/* Core Type & Archetype Banner */}
            <div className="p-3 rounded-xl bg-card border border-border/80 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div>
                <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold block">
                  Enneagram Core & Wing
                </span>
                <span className="text-base font-extrabold text-foreground tracking-tight">
                  Type {profile.core_type || 8}
                  {profile.wing ? `w${profile.wing}` : ""}{" "}
                  <span className="text-xs font-normal text-muted-foreground ml-1">
                    ({profile.archetype || "The Challenger / Strategic Catalyst"})
                  </span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right sm:text-right">
                  <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold block">
                    Instinctual Subtype
                  </span>
                  <Badge variant="outline" className="text-xs font-medium border-purple-500/40 text-purple-700 dark:text-purple-300">
                    {profile.subtype || "Social Subtype"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* 5 Lens Dimension Meters */}
            <div>
              <div className="flex items-center justify-between text-xs font-semibold text-foreground mb-2">
                <span>The 5 Core Dimensions</span>
                <span className="text-muted-foreground text-[11px]">Normalized Scale (0-100)</span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-2.5">
                {/* Lens 1: Energy */}
                <DimensionCard
                  title="Energy Dynamics"
                  value={profile.energy || 85}
                  icon={Zap}
                  colorClass="text-amber-500 bg-amber-500/10"
                  barColor="bg-amber-500"
                  subtitle="Resilience & Vitality"
                />

                {/* Lens 2: Social */}
                <DimensionCard
                  title="Social / Relating"
                  value={profile.social || 80}
                  icon={Users}
                  colorClass="text-blue-500 bg-blue-500/10"
                  barColor="bg-blue-500"
                  subtitle="Connection & Trust"
                />

                {/* Lens 3: Processing */}
                <DimensionCard
                  title="Cognitive Depth"
                  value={profile.processing || 88}
                  icon={Brain}
                  colorClass="text-purple-500 bg-purple-500/10"
                  barColor="bg-purple-500"
                  subtitle="Analysis & Strategy"
                />

                {/* Lens 4: Action */}
                <DimensionCard
                  title="Action / Drive"
                  value={profile.action || 92}
                  icon={Sparkles}
                  colorClass="text-rose-500 bg-rose-500/10"
                  barColor="bg-rose-500"
                  subtitle="Execution & Impact"
                />

                {/* Lens 5: Resonance */}
                <DimensionCard
                  title="Emotional Resonance"
                  value={profile.resonance || 75}
                  icon={HeartHandshake}
                  colorClass="text-emerald-500 bg-emerald-500/10"
                  barColor="bg-emerald-500"
                  subtitle="Empathy & Alignment"
                />
              </div>
            </div>

            {/* Executive Coaching & Engagement Hook */}
            {profile.coaching_tips && (
              <div className="p-3 rounded-xl bg-indigo-500/5 dark:bg-indigo-950/20 border border-indigo-500/20 text-xs">
                <span className="font-semibold text-indigo-950 dark:text-indigo-200 block mb-0.5">
                  💡 Executive Engagement & Coaching Insight:
                </span>
                <p className="text-muted-foreground leading-relaxed">
                  {profile.coaching_tips}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Sliders className="w-5 h-5 text-indigo-600" />
              Configure Five Lens™ Psychometrics
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            {/* Stakeholder Role */}
            <div>
              <label className="font-semibold text-foreground block mb-1">
                Account Stakeholder Role
              </label>
              <select
                value={formRole}
                onChange={(e) => setFormRole(e.target.value)}
                className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs focus:ring-1 focus:ring-primary"
              >
                <option value="sponsor">👑 Executive Sponsor (Budget sign-off)</option>
                <option value="decision_maker">🎯 Decision Maker</option>
                <option value="champion">⚡ Internal Champion</option>
                <option value="participant">🎓 Cohort Learner / Accredited Coach</option>
                <option value="influencer">🤝 Trusted Influencer</option>
              </select>
            </div>

            {/* Core Type & Wing */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-semibold text-foreground block mb-1">
                  Enneagram Core Type (1-9)
                </label>
                <select
                  value={formValues.core_type || 8}
                  onChange={(e) =>
                    setFormValues({ ...formValues, core_type: Number(e.target.value) })
                  }
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((t) => (
                    <option key={t} value={t}>
                      Type {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">
                  Dominant Wing
                </label>
                <select
                  value={formValues.wing || 7}
                  onChange={(e) =>
                    setFormValues({ ...formValues, wing: Number(e.target.value) })
                  }
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((w) => (
                    <option key={w} value={w}>
                      w{w}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Archetype & Subtype */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="font-semibold text-foreground block mb-1">
                  Archetype Label
                </label>
                <input
                  type="text"
                  value={formValues.archetype || ""}
                  onChange={(e) =>
                    setFormValues({ ...formValues, archetype: e.target.value })
                  }
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs"
                  placeholder="e.g. The Challenger / Strategic Leader"
                />
              </div>

              <div>
                <label className="font-semibold text-foreground block mb-1">
                  Instinctual Subtype
                </label>
                <select
                  value={formValues.subtype || "Social"}
                  onChange={(e) =>
                    setFormValues({ ...formValues, subtype: e.target.value })
                  }
                  className="w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-xs shadow-xs"
                >
                  <option value="Social">Social</option>
                  <option value="Self-Preservation">Self-Preservation</option>
                  <option value="One-to-One">One-to-One (Sexual)</option>
                </select>
              </div>
            </div>

            {/* Sliders for 5 Lenses */}
            <div className="space-y-3 pt-2 border-t">
              <span className="font-semibold text-foreground block">
                5-Lens Dimensional Scores (0 - 100)
              </span>

              <SliderItem
                label="Energy Dynamics"
                val={formValues.energy || 80}
                onChange={(val) => setFormValues({ ...formValues, energy: val })}
              />
              <SliderItem
                label="Social / Relating"
                val={formValues.social || 80}
                onChange={(val) => setFormValues({ ...formValues, social: val })}
              />
              <SliderItem
                label="Cognitive Depth"
                val={formValues.processing || 85}
                onChange={(val) => setFormValues({ ...formValues, processing: val })}
              />
              <SliderItem
                label="Action / Execution"
                val={formValues.action || 90}
                onChange={(val) => setFormValues({ ...formValues, action: val })}
              />
              <SliderItem
                label="Emotional Resonance"
                val={formValues.resonance || 75}
                onChange={(val) => setFormValues({ ...formValues, resonance: val })}
              />
            </div>

            {/* Coaching Tips */}
            <div className="pt-2 border-t">
              <label className="font-semibold text-foreground block mb-1">
                Executive Coaching & Interaction Notes
              </label>
              <textarea
                rows={3}
                value={formValues.coaching_tips || ""}
                onChange={(e) =>
                  setFormValues({ ...formValues, coaching_tips: e.target.value })
                }
                className="w-full rounded-md border border-input bg-background p-2 text-xs shadow-xs"
                placeholder="What communication style works best with this executive? What coaching hooks are most effective?"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleSave}>
              Save 5-Lens Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DimensionCard = ({
  title,
  value,
  icon: Icon,
  colorClass,
  barColor,
  subtitle,
}: {
  title: string;
  value: number;
  icon: any;
  colorClass: string;
  barColor: string;
  subtitle: string;
}) => (
  <div className="p-2.5 rounded-xl border border-border/70 bg-card flex flex-col justify-between">
    <div className="flex items-center justify-between mb-1">
      <div className={`p-1.5 rounded-lg ${colorClass}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span className="font-bold text-sm text-foreground">{value}%</span>
    </div>
    <div>
      <span className="font-semibold text-[11px] block leading-tight text-foreground">
        {title}
      </span>
      <span className="text-[10px] text-muted-foreground block mb-1.5">
        {subtitle}
      </span>
      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  </div>
);

const SliderItem = ({
  label,
  val,
  onChange,
}: {
  label: string;
  val: number;
  onChange: (val: number) => void;
}) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-muted-foreground w-36 truncate">{label}</span>
    <input
      type="range"
      min="0"
      max="100"
      value={val}
      onChange={(e) => onChange(Number(e.target.value))}
      className="flex-1 accent-indigo-600 h-1.5 bg-muted rounded-lg"
    />
    <span className="font-mono font-bold w-10 text-right">{val}%</span>
  </div>
);
